import { RuleResource } from 'components/rules/rules.types';
import { AllConditionPredicates } from './rules.types';
import { isArrayOf, isNumber, isString } from 'lib/typeguards';

const subjectMap: Record<string, string> = {
    type: 'actionType',
    'context.location._id': 'locationIds',
    'context.clientPlatform': 'clientPlatforms',
    'when.daypart': 'dayPart',
    'when.dayofweek': 'dayOfWeek',
    'when.weekofyear': 'weekOfYear',
    'when.monthofyear': 'monthOfYear',
    'context.user.primaryPlatform': 'primaryPlatform',
    'metadata.value.amount': 'amount',
    'metadata.order.scenario': 'scenario',
    'metadata.channel': 'channel',
    'metadata.audienceId': 'audienceIds',
    'metadata.schemeId': 'schemeId',
    'metadata.perkId': 'perkId',
    'metadata.voucherId': 'voucherId',
    'metadata.order.payments': 'paymentMethod',
    'metadata.credential.id': 'emailEndsWith',
    'metadata.lengthOfStayNights': 'stayLength',
    'LEN(metadata.guests)': 'guestsNumber',
    'WEEKDAY(metadata.departure)': 'departureDaysOfWeek',
    'WEEKDAY(metadata.arrival)': 'arrivalDaysOfWeek',
    'ANY(metadata.guests.email)': 'guestEmailEndsWith',
    'ANY(metadata.booker.email)': 'bookerEmailEndsWith',
    'metadata.status': 'status',
    'metadata.ratePlan': 'ratePlan',
    'metadata.cost': 'cost',
    'ANY(metadata.favouriteLocations)': 'favouriteLocationIds'
};

export function flattenRuleConditions(rule: Partial<Pick<RuleResource, 'conditions'>>): Record<string, any> {
    return rule?.conditions?.reduce((acc, condition) => {
        const { subject, predicate, object } = condition;
        let key: string;
        let value: string | string[] | Date;
        if (predicate === 'HAS_CATEGORY_ID' || predicate === 'HAS_PRODUCT_ID') {
            key = predicate === 'HAS_CATEGORY_ID' ? 'categoryIds' : 'productIds';
            value = isArrayOf(isString, object) ? object : undefined;
            // Would be simpler if we could pass/parse an array of dates instead of all that
        } else if (subject === 'metadata.arrival' || subject === 'metadata.departure') {
            if (!isString(object)) {
                return acc;
            }
            value = new Date(object);
            switch (subject) {
                case 'metadata.arrival':
                    if (predicate === 'GREATER') {
                        key = 'arrivalDateFrom';
                    } else {
                        key = 'arrivalDateTo';
                    }
                    break;
                case 'metadata.departure':
                    if (predicate === 'GREATER') {
                        key = 'departureDateFrom';
                    } else {
                        key = 'departureDateTo';
                    }
                    break;
            }
        } else {
            key = subjectMap[subject] || subject;
            value = parseRuleConditionValue(key, object);
        }
        const parsedPredicate = parseRuleConditionPredicate(key, predicate);
        return { ...acc, [key]: value, ...(parsedPredicate ?? {}) };
    }, {});
}

function parseRuleConditionValue(key: string, value: any): string | string[] {
    switch (key) {
        case 'dayOfWeek':
        case 'monthOfYear':
            return value.toString();
        case 'dayPart':
            return value[0];
        case 'locationIds':
        case 'clientPlatforms':
        case 'audienceIds':
        case 'favouriteLocationIds':
            return isArrayOf(isString, value) ? value : [value];
        case 'arrivalDaysOfWeek':
        case 'departureDaysOfWeek':
            return isArrayOf(isNumber, value) ? value.map(day => day.toString()) : undefined;
        default:
            return value;
    }
}

function parseRuleConditionPredicate(key: string, predicate: AllConditionPredicates): Record<string, string> {
    switch (key) {
        case 'amount':
            return { amountPredicate: predicate };
        case 'cost':
            return { costPredicate: predicate };
        case 'guestsNumber':
            return { guestsNumberPredicate: predicate };
        case 'stayLength':
            return { stayLengthPredicate: predicate };
        default:
            return undefined;
    }
}
